import React, { useState } from 'react';
import { Auth } from 'aws-amplify';
import './Forms.css';

const LoginForm = ({ onSignIn }) => {
  const [formState, setFormState] = useState('signin'); // signin or signup or confirm
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    code: '',
    firstName: '',
    lastName: ''
  });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      if (formState === 'signin') {
        await Auth.signIn(formData.email, formData.password);
        setMessage('Sign in successful!');
        // Call the onSignIn callback if provided
        if (onSignIn) {
          onSignIn();
        }
      } else if (formState === 'signup') {
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Passwords do not match');
        }
        
        // Check password complexity
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
        if (!passwordRegex.test(formData.password)) {
          throw new Error('Password must be at least 8 characters long and include uppercase, lowercase, and numbers');
        }
        
        // Validate required fields for signup
        if (!formData.firstName.trim() || !formData.lastName.trim()) {
          throw new Error('First name and last name are required');
        }
        
        await Auth.signUp({
          username: formData.email, // Use email as username
          password: formData.password,
          attributes: {
            email: formData.email,
            given_name: formData.firstName.trim(),
            family_name: formData.lastName.trim()
          }
        });
        
        setMessage('Sign up successful! Please check your email for verification code.');
        setFormState('confirm');
      } else if (formState === 'confirm') {
        await Auth.confirmSignUp(formData.email, formData.code);
        setMessage('Email verified! You can now sign in.');
        setFormState('signin');
      } else if (formState === 'forgotPassword') {
        await Auth.forgotPassword(formData.email);
        setMessage('Password reset code sent to your email.');
        setFormState('resetPassword');
      } else if (formState === 'resetPassword') {
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Passwords do not match');
        }
        
        // Check password complexity
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
        if (!passwordRegex.test(formData.password)) {
          throw new Error('Password must be at least 8 characters long and include uppercase, lowercase, and numbers');
        }
        
        await Auth.forgotPasswordSubmit(
          formData.email,
          formData.code,
          formData.password
        );
        setMessage('Password has been reset successfully. You can now sign in with your new password.');
        setFormState('signin');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <div className="form-tabs">
        <button 
          className={formState === 'signin' ? 'active' : ''} 
          onClick={() => setFormState('signin')}
          disabled={formState === 'confirm' || formState === 'forgotPassword' || formState === 'resetPassword'}
        >
          Sign In
        </button>
        <button 
          className={formState === 'signup' ? 'active' : ''} 
          onClick={() => setFormState('signup')}
          disabled={formState === 'confirm' || formState === 'forgotPassword' || formState === 'resetPassword'}
        >
          Sign Up
        </button>
      </div>

      {error && <div className="form-error">{error}</div>}
      {message && <div className="form-message">{message}</div>}

      <form onSubmit={handleSubmit}>
        {(formState === 'signin' || formState === 'signup') && (
          <>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            
            {formState === 'signup' && (
              <>
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </>
            )}
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
              />
              {formState === 'signup' && (
                <div className="password-requirements">
                  Password must be at least 8 characters long and include uppercase, lowercase, and numbers.
                </div>
              )}
            </div>
          </>
        )}

        {formState === 'signup' && (
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
            />
          </div>
        )}

        {formState === 'confirm' && (
          <div className="form-group">
            <label htmlFor="code">Verification Code</label>
            <input
              type="text"
              id="code"
              name="code"
              value={formData.code}
              onChange={handleChange}
              required
            />
          </div>
        )}

        {formState === 'forgotPassword' && (
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
        )}

        {formState === 'resetPassword' && (
          <>
            <div className="form-group">
              <label htmlFor="code">Verification Code</label>
              <input
                type="text"
                id="code"
                name="code"
                value={formData.code}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">New Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
              />
              <div className="password-requirements">
                Password must be at least 8 characters long and include uppercase, lowercase, and numbers.
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
              />
            </div>
          </>
        )}

        <button type="submit" className="form-button" disabled={loading}>
          {loading ? 'Processing...' : 
            formState === 'signin' ? 'Sign In' : 
            formState === 'signup' ? 'Sign Up' : 
            formState === 'confirm' ? 'Verify' :
            formState === 'forgotPassword' ? 'Send Code' :
            'Reset Password'}
        </button>
      </form>

      {formState === 'signin' && (
        <button 
          className="form-link" 
          onClick={() => setFormState('forgotPassword')}
        >
          Forgot Password?
        </button>
      )}

      {formState === 'confirm' && (
        <button 
          className="form-link" 
          onClick={() => setFormState('signup')}
        >
          Back to Sign Up
        </button>
      )}

      {formState === 'forgotPassword' && (
        <button 
          className="form-link" 
          onClick={() => setFormState('signin')}
        >
          Back to Sign In
        </button>
      )}

      {formState === 'resetPassword' && (
        <button 
          className="form-link" 
          onClick={() => setFormState('signin')}
        >
          Back to Sign In
        </button>
      )}
    </div>
  );
};

export default LoginForm;
